import lodashSet from "lodash/set";
import React, { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  apiRequestContractHandler,
  apiRequestContractHandlerPropsInput,
} from "shared/api/apiRequestContractHandler";
import { postSocietyProfileWorkflowInstanceIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance/workflowInstanceId";
import { getSocietyWorkflowStateContract } from "shared/api/contracts/society/societyId/workflow/workflowId/state";
import { type SocietyWorkflowInstanceBodyInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { PillarFormDynamicInputComponent } from "shared/components/pillar-form/PillarFormDynamicInputComponentsMapper";
import PillarModalBody, {
  ModalMaxWidth,
  ModalMinWidth,
} from "shared/components/pillar-modal/NewPillarModalBody";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowStateView } from "shared/mappers/database/workflow/workflow-state";
import { type WorkflowStateTransition } from "shared/workflow/workflow-state";

const AdminSetWorkflowStateModal = ({
  workflowInstance,
  control,
  refetchCallback,
  testid,
}: {
  control: PillarModalControl;
  workflowInstance: WorkflowInstanceView;
  refetchCallback?: () => Promise<void>;
  testid: string;
}) => {
  const { society } = useCustomSession();
  const [selectedState, setSelectedState] = useState<WorkflowStateView | null>(
    null,
  );

  const adminFormElements = useMemo(
    () => selectedState?.adminForm?.formElements ?? [],
    [selectedState, selectedState?.name],
  );

  const selectedStateTransitions = useMemo<WorkflowStateTransition[]>(() => {
    if (!selectedState) {
      return [];
    }

    if (
      "transitions" in selectedState &&
      Array.isArray(selectedState.transitions)
    ) {
      return selectedState.transitions;
    }

    if (
      "condition" in selectedState &&
      Array.isArray(selectedState.condition)
    ) {
      return selectedState.condition;
    }

    return [];
  }, [selectedState]);

  const hasTransitionEvents = selectedStateTransitions.length > 0;

  const adminSetWorkflowStateFormSchema = useMemo(
    () =>
      Yup.object({
        setState: Yup.string().required("Workflow state is required"),
        ...(hasTransitionEvents
          ? { eventName: Yup.string().required("Event name is required") }
          : {}),
      }),
    [hasTransitionEvents],
  );

  const workflowId = workflowInstance.workflow?.workflowId;

  const handleSubmit = useCallback(
    async (form: Partial<SocietyWorkflowInstanceBodyInput>) => {
      const body: SocietyWorkflowInstanceBodyInput = {
        ...form,
        eventName:
          typeof form.eventName === "string" && form.eventName.trim().length > 0
            ? form.eventName
            : "AdminSetState", // Default event name if not provided
        payloadValue: workflowInstance.workflowInstancePayload?.payloadValue,
        profileId: workflowInstance.profile!.profileId,
        workflowInstancePayloadId: workflowInstance.workflowInstancePayloadId,
        formId: workflowInstance.workflowInstancePayload?.form?.formId,
      };

      await apiRequestContractHandler(
        postSocietyProfileWorkflowInstanceIdContract,
        {
          params: {
            societyId: society!.societyId,
            profileId: workflowInstance.profile!.profileId,
            workflowInstanceId: workflowInstance.workflowInstanceId || 0,
            workflowId: workflowInstance.workflow?.workflowId || 0,
          },
          body,
        },
      );

      control.setIsOpen(false);
      if (refetchCallback) {
        await refetchCallback();
      }
    },
    [
      workflowInstance,
      control,
      adminFormElements,
      society,
      workflowInstance.profile,
      refetchCallback,
    ],
  );

  const dynamicInitialValues = adminFormElements.reduce<
    Record<string, unknown>
  >((acc, el) => {
    if (el.initialValue !== undefined) {
      lodashSet(acc, el.name, el.initialValue);
    }
    return acc;
  }, {});

  return (
    <PillarModalBody
      control={control}
      title="Update Workflow State"
      maxW={ModalMaxWidth.Xl_1200}
      minW={ModalMinWidth.Lg_900}
      testid={`${testid}-admin-set-workflow-state-modal`}
    >
      <PillarForm
        overrideInitialValues={{
          setState: "",
          eventName: "",
          ...dynamicInitialValues,
        }}
        handleSubmit={handleSubmit}
        className="flex w-full flex-col items-center"
        yupValidation={adminSetWorkflowStateFormSchema}
      >
        <PillarForm.SelectComponent<
          WorkflowStateView,
          string,
          Record<string, unknown>
        >
          additionalClasses="w-full"
          name="setState"
          label="Workflow State"
          placeholder="Select a state..."
          queryCallback={async ({ text }) =>
            await apiRequestContractHandlerPropsInput({
              contract: getSocietyWorkflowStateContract,
              inputs: {
                params: {
                  societyId: society!.societyId,
                  workflowId: workflowId!,
                },
              },
              filters: text ? { name: String(text) } : undefined,
            })
          }
          labelCallback={(s) =>
            `${s.name} (${s.description}) - ${s.type.toUpperCase()}`
          }
          valueCallback={(s) => s.name}
          onChangeCallback={(newValue, _actionMeta, results) => {
            const selectedValue =
              newValue && "value" in newValue ? newValue.value : undefined;
            setSelectedState(
              results.find((r) => r.name === selectedValue) ?? null,
            );
          }}
          testid={`${testid}-admin-set-workflow-state-modal-state-select`}
        />

        {hasTransitionEvents ? (
          <PillarForm.SelectComponent<
            WorkflowStateTransition,
            string,
            Record<string, unknown>
          >
            additionalClasses="w-full"
            key={selectedState?.name ?? "no-state"}
            name="eventName"
            label="Event Name"
            required
            placeholder="Select an event..."
            queryCallback={async ({ text }) => {
              const lowerText = String(text ?? "").toLowerCase();
              const results = selectedStateTransitions.filter((transition) =>
                transition.event.toLowerCase().includes(lowerText),
              );

              return {
                results,
                totalResults: results.length,
                page: 1,
                pageSize: results.length,
              };
            }}
            labelCallback={(transition) =>
              `${transition.event} -> ${transition.next}`
            }
            valueCallback={(transition) => transition.event}
            testid={`${testid}-admin-set-workflow-state-modal-event-select`}
          />
        ) : null}

        {adminFormElements.map((el, idx) => (
          <PillarFormDynamicInputComponent key={el.name ?? idx} element={el} />
        ))}

        <PillarForm.LoadingSubmitButton
          label="Save"
          className="mt-2"
          testid={`${testid}-admin-set-workflow-state-modal-submit`}
        />
      </PillarForm>
    </PillarModalBody>
  );
};

export default AdminSetWorkflowStateModal;
